import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Sparkles, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== pw2) return setErr("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password reset! Please log in.");
      nav("/login");
    } catch (err) {
      setErr(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 grid-noise">
        <div className="brutal bg-white p-6 max-w-md text-center">
          <h2 className="font-heading font-extrabold text-2xl">Missing token</h2>
          <p className="text-sm text-muted-foreground font-medium mt-2">This reset link looks broken.</p>
          <Link to="/forgot-password" className="mt-4 inline-block px-4 py-2 bg-primary border-2 border-foreground rounded-md font-bold shadow-brutal-sm">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background grid-noise flex items-center justify-center p-4">
      <Link to="/login" className="absolute top-6 left-6 flex items-center gap-2 font-bold hover:underline">
        <ArrowLeft className="w-4 h-4" /> Back to login
      </Link>

      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-11 h-11 bg-primary border-2 border-foreground rounded-md shadow-brutal flex items-center justify-center">
              <Sparkles className="w-6 h-6" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-extrabold text-2xl">DmFlio</span>
          </div>
          <h1 className="text-3xl font-extrabold">Choose a new password</h1>
        </div>

        <form onSubmit={onSubmit} className="brutal bg-white p-6 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">New password</label>
            <input type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-pw" className="brutal-input w-full" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">Confirm</label>
            <input type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-pw2" className="brutal-input w-full" />
          </div>
          {err && <div data-testid="reset-error" className="text-sm font-semibold text-destructive bg-destructive/10 border-2 border-destructive p-2 rounded-md">{err}</div>}
          <button type="submit" disabled={busy} data-testid="reset-submit" className="w-full bg-primary border-2 border-foreground rounded-md py-3 font-bold shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 transition-all disabled:opacity-50">
            {busy ? "Saving…" : "Reset password"}
          </button>
        </form>
      </div>
    </div>
  );
}
